/**
 * loadEmailFile: the file-ingestion path, calling the engine parsers
 * directly. Byte-faithful read, Outlook `.msg` + `.pst`/`.ost` unpack and
 * mbox splitting, all in the page. Nothing is uploaded and nothing is
 * fetched, which is why every container format above has a reader in this
 * tree instead of a service behind it.
 *
 * Returns a discriminated result the shell routes:
 *   single → inspect one message
 *   mbox   → file a whole mailbox (multiple messages) in the Mailbox view
 *   error  → a stable failure CODE; the display site localizes it (i18n err.*)
 */
import type { ZipBudget } from "../parsing/zip-reader";
import { readParserConfig } from "../perf/budgets";
import { bytesToByteString } from "../utils/buffer";
import { iterMbox, mboxBlockToMessage, splitMbox } from "../utils/mbox";

/** Stable failure codes. Localized at the display sites, never here. */
export type LoadErrorCode =
  | "empty"
  | "too-large"
  | "unreadable"
  | "msg-unreadable"
  | "pst-unreadable"
  | "pst-empty"
  | "zip-unreadable"
  | "zip-empty"
  // The three below are refusals by `loadEmailFiles`, decided from File.size
  // and the input position alone. A file carrying one of these was never
  // opened, which is the point: they exist so a multi-file drop cannot buffer
  // itself to death before the corpus cap is consulted.
  | "drop-too-large"
  | "too-many-files"
  | "cap-reached"
  // Not a refusal: this file WAS opened, and its fan-out stopped at the
  // per-file budget. Distinct from "cap-reached", which means never opened,
  // because telling a reader "not opened" about a file whose first 250
  // messages are on screen is simply false.
  | "archive-truncated";

export interface LoadError {
  code: LoadErrorCode;
  /** The live byte cap that rejected it ("too-large", "drop-too-large"). */
  capBytes?: number;
  /** The live count cap that rejected it ("too-many-files", "cap-reached"). */
  capFiles?: number;
}

export type LoadResult =
  | { kind: "single"; source: string }
  | {
      kind: "mbox";
      /**
       * The admitted messages, already split. There is deliberately NO joined
       * `source` string beside them.
       *
       * There used to be one, and it was re-split by the Corpus intake, so an
       * admitted mailbox was materialised twice and the second split was
       * unbounded. The obvious repair, joining only the admitted parts back
       * into an mbox, was worse than the cost it saved: an mbox message keeps
       * its own `From <addr> <date>` envelope line, so re-framing it wrote a
       * fabricated `From mime-smith@localhost` envelope on top and quoted the
       * REAL one down to `>From alice@example …`, which is not a header field
       * at all. Every message of every plain `.mbox` opened that way began
       * with a junk line and had the mailbox's recorded sender and date
       * destroyed.
       *
       * A mailbox is its messages. One representation cannot drift from or
       * corrupt the other if there is only one.
       */
      parts: string[];
      /**
       * Set when the archive held more messages than `maxMessages` allowed.
       * `parts` is the admitted prefix, and the rest were never synthesised:
       * this is a "stopped here" marker, not a count of what was dropped,
       * because counting the remainder would mean doing the work being
       * refused.
       */
      truncated?: true;
    }
  | ({ kind: "error" } & LoadError);

/**
 * How many messages one file may fan out to before the reader is told the
 * archive was cut short.
 *
 * The multi-file loader's own 250-block cap governs the FLATTENED result of a
 * whole drop, which bounds many files and not the fan-out inside one of them.
 * A single `.pst`, `.zip` or `.mbox` admitted by the byte cap can still declare
 * hundreds of thousands of messages, and each one synthesised here costs a
 * decode of both body variants, every attachment and a full `.eml` string
 * before anything downstream can refuse it. Found by an independent review on
 * 2026-08-26.
 */
export const MAX_MESSAGES_PER_FILE = 250;

/** Options a caller can use to spend less than the default per-file budget. */
export interface LoadOptions {
  /** Stop the archive fan-out here. Defaults to {@link MAX_MESSAGES_PER_FILE}. */
  maxMessages?: number;
}

/** Generous client-side floor so a multi-GB drop can't OOM the tab. */
const DEFAULT_MAX_BYTES = 80 * 1024 * 1024;

/**
 * The live drop cap: the Settings parser limit (maxMessageBytes, persisted to
 * emlx:parser-config) can RAISE the guard above the default, so the home
 * banner's "raise the size limits in settings" link is honest. It never
 * lowers the floor; below it the parser stamps its own size defect instead.
 */
export function dropCapBytes(): number {
  try {
    return Math.max(DEFAULT_MAX_BYTES, readParserConfig().maxMessageBytes);
  } catch {
    return DEFAULT_MAX_BYTES;
  }
}

/** i18n key per failure code (keys exist in all four locale tables). */
export const LOAD_ERROR_KEY: Record<LoadErrorCode, string> = {
  empty: "err.empty",
  "too-large": "err.tooLarge",
  unreadable: "err.unreadable",
  "msg-unreadable": "err.msgUnreadable",
  "pst-unreadable": "err.pstUnreadable",
  "pst-empty": "err.pstEmpty",
  "zip-unreadable": "err.zipUnreadable",
  "zip-empty": "err.zipEmpty",
  "drop-too-large": "err.dropTooLarge",
  "too-many-files": "err.tooManyFiles",
  "cap-reached": "err.capReached",
  "archive-truncated": "err.archiveTruncated",
};

/** Localize a load failure with the active t(). Interpolates whichever cap the
 *  code carries: {mb} for the byte limits, {n} for the count limits. */
export function loadErrorMessage(err: LoadError, t: (key: string) => string): string {
  const msg = t(LOAD_ERROR_KEY[err.code]);
  const mb = Math.round((err.capBytes ?? DEFAULT_MAX_BYTES) / (1024 * 1024));
  return msg.replace("{mb}", String(mb)).replace("{n}", String(err.capFiles ?? 0));
}

/**
 * Map bytes → one UTF-16 code unit each (binary string) so the parser's
 * byte-for-byte round-trip invariant holds for 8bit/binary parts and non-UTF-8
 * charset bodies.
 *
 * This was a private copy of the loop until 2026-08-28, when the sample-exhibit
 * fetch needed the same mapping and the paste path needed the encode that feeds
 * it. Three spellings of one rule is how the rule drifts, so the loop now lives
 * beside the invariant it serves, in `utils/buffer.ts`, and this is the name
 * the file has always called it by.
 */
const toBinaryString = bytesToByteString;

function magic(buf: Uint8Array, bytes: number[]): boolean {
  if (buf.length < bytes.length) return false;
  for (let i = 0; i < bytes.length; i++) if (buf[i] !== bytes[i]) return false;
  return true;
}

export async function loadEmailFile(
  file: File,
  opts: LoadOptions = {},
): Promise<LoadResult> {
  // The fan-out budget. Every archive branch below stops HERE rather than
  // materialising the archive and letting a downstream cap trim it.
  const maxMessages = Math.max(1, opts.maxMessages ?? MAX_MESSAGES_PER_FILE);
  /** Ask one past the budget: the extra item is how "there was more" is known. */
  const probe = maxMessages + 1;
  const asMbox = (parts: string[], forceTruncated = false): LoadResult => {
    const over = parts.length > maxMessages;
    const kept = over ? parts.slice(0, maxMessages) : parts;
    return over || forceTruncated
      ? { kind: "mbox", parts: kept, truncated: true }
      : { kind: "mbox", parts: kept };
  };
  if (file.size === 0) return { kind: "error", code: "empty" };
  const cap = dropCapBytes();
  if (file.size > cap) {
    return { kind: "error", code: "too-large", capBytes: cap };
  }

  let buf: Uint8Array;
  try {
    buf = new Uint8Array(await file.arrayBuffer());
  } catch {
    return { kind: "error", code: "unreadable" };
  }

  // Outlook .msg (OLE/CFBF): D0 CF 11 E0 A1 B1 1A E1 → synthesise one .eml.
  if (magic(buf, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    try {
      const { msgToEml } = await import("../parsing/msg/msg");
      const eml = msgToEml(buf);
      return eml
        ? { kind: "single", source: eml }
        : { kind: "error", code: "msg-unreadable" };
    } catch {
      return { kind: "error", code: "msg-unreadable" };
    }
  }

  // Outlook .pst / .ost archive: "!BDN" (21 42 44 4E) → a mailbox of messages.
  if (magic(buf, [0x21, 0x42, 0x44, 0x4e])) {
    try {
      const { pstToEmls } = await import("../parsing/pst/pst");
      const emls = pstToEmls(buf, { maxMessages: probe });
      if (emls && emls.length > 1) return asMbox(emls.map((e) => e.eml));
      if (emls && emls.length === 1) return { kind: "single", source: emls[0]!.eml };
      return { kind: "error", code: "pst-empty" };
    } catch {
      return { kind: "error", code: "pst-unreadable" };
    }
  }

  // Zip archive: extract message-shaped members and file them as one mailbox.
  //
  // Both signatures, not just the local-file header. An EMPTY zip is a valid
  // zip that begins with the end-of-central-directory record PK\x05\x06, and
  // matching only PK\x03\x04 sent it down the plain-text path: the reader got
  // a "message" made of binary noise instead of "this archive has nothing in
  // it".
  if (magic(buf, [0x50, 0x4b, 0x03, 0x04]) || magic(buf, [0x50, 0x4b, 0x05, 0x06])) {
    try {
      const { readZip, MAX_ARCHIVE_DEPTH } = await import("../parsing/zip-reader");
      const parts: string[] = [];
      // One subtree lost to depth or budget does not make the rest of the
      // archive's messages any less real, so the walk always keeps going;
      // this only records that the import is not the whole tree, so the
      // reader is told rather than handed a quietly incomplete mailbox.
      let dropped = false;
      // Shared across the WHOLE recursion, not one per call: `readZip`'s
      // budget check is a running tally against this object, so a nested
      // zip's declared bytes count against the SAME cap its parent and
      // siblings draw from. Passing a fresh budget per level (or omitting
      // it) is exactly the bug this closes — see `ZipBudget`'s docblock.
      const budget: ZipBudget = { used: 0 };
      // Stops at the probe across the WHOLE archive tree, not per member or
      // per nesting level: a zip of one thousand single-message .eml files
      // fans out exactly as far as one .mbox member holding a thousand
      // messages, or one nested zip holding a thousand .eml entries.
      //
      // `zip-reader.ts` classifies a `.zip` member with kind="zip" and
      // decodes its bytes precisely so a caller can recurse into it; this is
      // that caller, bounded by the depth the reader already exports and
      // documents but, until now, nothing consumed.
      const walk = (bytes: Uint8Array, depth: number): void => {
        const entries = readZip(bytes, budget);
        for (const entry of entries) {
          if (parts.length >= probe) return;
          if (entry.skipped || entry.bytes.length === 0) {
            // Most skips cost nothing: a directory entry, a README, a logo
            // were never evidence, and an archive that carries them is still
            // completely imported. `undecoded` is the other kind: a
            // message-shaped member (an `.eml`, an `.mbox`, or a nested zip
            // whose whole subtree goes with it) that the reader listed and
            // could not open, today because its compression method is not
            // one of the two this build decodes. That is the same loss as a
            // depth or budget drop below, so it reports the same way rather
            // than leaving the reader a mailbox that looks whole.
            if (entry.undecoded) dropped = true;
            continue;
          }
          if (entry.kind === "eml" || entry.kind === "mbox") {
            const text = toBinaryString(entry.bytes);
            for (const m of splitMbox(text)) {
              parts.push(mboxBlockToMessage(m));
              if (parts.length >= probe) return;
            }
          } else if (entry.kind === "zip") {
            if (depth >= MAX_ARCHIVE_DEPTH) {
              // Past the depth this reader recurses to: real evidence,
              // never opened. Distinct from a probe cutoff (there IS more
              // to see) in cause but identical in what the reader needs to
              // know, so it reports through the same `truncated` channel.
              dropped = true;
              continue;
            }
            try {
              walk(entry.bytes, depth + 1);
            } catch {
              // One corrupt or over-budget nested archive costs only its own
              // subtree, but that subtree's messages are gone, not merely
              // unlisted: the rest of the drop the reader chose is not
              // theirs to lose, and the loss is not theirs to hide either.
              dropped = true;
            }
          }
        }
      };
      walk(buf, 0);
      if (parts.length === 0) {
        return dropped
          ? { kind: "error", code: "archive-truncated", capFiles: 0 }
          : { kind: "error", code: "zip-empty" };
      }
      if (parts.length === 1 && !dropped) return { kind: "single", source: parts[0]! };
      return asMbox(parts, dropped);
    } catch {
      return { kind: "error", code: "zip-unreadable" };
    }
  }

  // Plain text (.eml / .mbox): walk the envelopes with the streaming splitter
  // and stop at the probe, so a mailbox admitted by the byte cap is never
  // materialised past the budget. `splitMbox` built the complete array first
  // and let the cap trim it afterwards, which is the work the cap exists to
  // refuse.
  //
  // The messages go downstream as messages. Nothing re-joins them into a
  // second mbox string for the Corpus intake to split a second time: see the
  // `mbox` variant of LoadResult for what that cost.
  const raw = toBinaryString(buf);
  const parts: string[] = [];
  for (const msg of iterMbox(raw)) {
    parts.push(mboxBlockToMessage(msg));
    if (parts.length >= probe) break;
  }
  if (parts.length === 0) return { kind: "error", code: "empty" };
  if (parts.length === 1) return { kind: "single", source: parts[0]! };
  return asMbox(parts);
}
